/**
 * Branding Editor (Super-Admin)
 * Configure runtime white-label settings for Internal + Client apps.
 *
 * Admin app styling remains JourFx; this is only a configuration UI.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Switch,
  Skeleton,
  useToast,
} from "@jourfx/ui";
import { Palette, Upload, Trash2, Save, Loader2 } from "lucide-react";
import { SuperAdminGuard } from "../components/SuperAdminGuard";
import { PageHeader } from "../components/PageHeader";

type BrandingLocale = "de" | "en";

const TEXT_FIELDS: Array<{ key: string; labelKey: string; helperKey?: string }> = [
  { key: "common.support.questions", labelKey: "texts.fields.supportHeading" },
  { key: "common.support.phone", labelKey: "texts.fields.supportPhone" },
  { key: "portalAccess.title", labelKey: "texts.fields.portalAccessTitle" },
  { key: "portalAccess.subtitle", labelKey: "texts.fields.portalAccessSubtitle" },
  { key: "portalAccess.form.helpLink", labelKey: "texts.fields.portalAccessHelpLink" },
  { key: "portalAccess.privacyNotice", labelKey: "texts.fields.portalAccessPrivacyNotice" },
  { key: "portalAccess.privacyModal.title", labelKey: "texts.fields.privacyModalTitle" },
  { key: "techcheck.title", labelKey: "texts.fields.techcheckTitle" },
  { key: "video.waitingRoom.technicalIssues", labelKey: "texts.fields.waitingRoomTechnicalIssues" },
  { key: "video.waitingRoom.leaveConfirm.hotlineInfo", labelKey: "texts.fields.leaveConfirmHotlineInfo" },
];

function isValidHexColor(value: string) {
  return /^#(?:[0-9a-fA-F]{6})$/.test(value.trim());
}

function normalizeHex(value: string) {
  return value.trim().toLowerCase();
}

function getConvexBaseUrl() {
  // HTTP actions (including logo endpoints) live on the Convex "site" URL.
  return (
    import.meta.env.VITE_CONVEX_SITE_URL ||
    import.meta.env.VITE_CONVEX_URL ||
    "http://localhost:3211"
  );
}

function getLogoUrl(kind: "primary" | "square" | "favicon", cacheBust: number) {
  const base = getConvexBaseUrl().replace(/\/$/, "");
  const pathMap = {
    primary: "/api/branding/logo-primary",
    square: "/api/branding/logo-square",
    favicon: "/api/branding/favicon",
  };
  return `${base}${pathMap[kind]}?v=${cacheBust}`;
}

function preloadImage(src: string, timeoutMs: number) {
  return new Promise<boolean>((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => {
      cleanup(false);
    }, timeoutMs);

    const cleanup = (ok: boolean) => {
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      resolve(ok);
    };

    img.onload = () => cleanup(true);
    img.onerror = () => cleanup(false);
    img.src = src;
  });
}

async function waitForRemoteLogoToBeStorageBacked(opts: {
  src: string;
  expectedStorageId: string;
  timeoutMs: number;
}) {
  const { src, expectedStorageId, timeoutMs } = opts;
  const start = Date.now();

  // Poll the logo endpoint until it stops serving the default fallback and is backed by storage.
  // This prevents the UI from briefly showing the local blob preview and then "reverting" to the
  // default SVG while Convex storage propagation finishes.
  for (let attempt = 0; Date.now() - start < timeoutMs; attempt++) {
    try {
      const res = await fetch(src, { method: "GET", cache: "no-store", mode: "cors" });
      if (res.ok) {
        const source = res.headers.get("x-logo-source"); // exposed by Convex HTTP router
        const etag = res.headers.get("etag") || "";
        const etagMentionsStorageId =
          expectedStorageId.length > 0 && etag.includes(expectedStorageId);

        if (source === "storage" && etagMentionsStorageId) {
          return true;
        }
      }
    } catch {
      // Mixed-content, DNS, CORS, etc. Keep polling until timeout and let caller handle.
    }

    // Simple backoff: 120ms -> ~650ms max
    const sleepMs = Math.min(120 + attempt * 60, 650);
    await new Promise((r) => setTimeout(r, sleepMs));
  }

  return false;
}

function BrandingContent() {
  const { t: tBranding } = useTranslation("branding");
  const { t: tCommon } = useTranslation("common");
  const { toast } = useToast();

  const branding = useQuery((api as any).admin.branding.getAdmin);
  const update = useMutation((api as any).admin.branding.update);
  const generateUploadUrl = useMutation((api as any).admin.branding.generateUploadUrl);
  const setLogo = useMutation((api as any).admin.branding.setLogo);

  const [locale, setLocale] = useState<BrandingLocale>("de");
  const [enabled, setEnabled] = useState(false);
  const [primaryHex, setPrimaryHex] = useState("#2f5b45");
  const [secondaryHex, setSecondaryHex] = useState("#f5f4f2");
  const [accentHex, setAccentHex] = useState("#3c6a52");
  const [fontFamily, setFontFamily] = useState<"lato">("lato");
  const [scale, setScale] = useState<"standard" | "large">("standard");
  const [textsDe, setTextsDe] = useState<Record<string, string>>({});
  const [textsEn, setTextsEn] = useState<Record<string, string>>({});

  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState<"primary" | "square" | "favicon" | null>(null);
  const primaryInputRef = useRef<HTMLInputElement | null>(null);
  const squareInputRef = useRef<HTMLInputElement | null>(null);
  const faviconInputRef = useRef<HTMLInputElement | null>(null);
  const [localPrimaryPreviewUrl, setLocalPrimaryPreviewUrl] = useState<string | null>(null);
  const [localSquarePreviewUrl, setLocalSquarePreviewUrl] = useState<string | null>(null);
  const [localFaviconPreviewUrl, setLocalFaviconPreviewUrl] = useState<string | null>(null);

  const updatedAt = branding?.updatedAt || 0;

  // Initialize local state from server
  useEffect(() => {
    if (!branding) return;
    setEnabled(!!branding.enabled);
    setPrimaryHex(branding.colors?.primaryHex || "#2f5b45");
    setSecondaryHex(branding.colors?.secondaryHex || "#f5f4f2");
    setAccentHex(branding.colors?.accentHex || "#3c6a52");
    setFontFamily(branding.typography?.fontFamily || "lato");
    setScale(branding.typography?.scale || "standard");
    setTextsDe(branding.texts?.de || {});
    setTextsEn(branding.texts?.en || {});
    setHasChanges(false);
  }, [branding]);

  // Cleanup local preview URLs
  useEffect(() => {
    return () => {
      if (localPrimaryPreviewUrl) URL.revokeObjectURL(localPrimaryPreviewUrl);
      if (localSquarePreviewUrl) URL.revokeObjectURL(localSquarePreviewUrl);
      if (localFaviconPreviewUrl) URL.revokeObjectURL(localFaviconPreviewUrl);
    };
  }, [localPrimaryPreviewUrl, localSquarePreviewUrl, localFaviconPreviewUrl]);

  const textState = locale === "de" ? textsDe : textsEn;
  const setTextState = locale === "de" ? setTextsDe : setTextsEn;

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!isValidHexColor(primaryHex)) errors.push(tBranding("colors.validation.primaryHex"));
    if (secondaryHex && !isValidHexColor(secondaryHex)) errors.push(tBranding("colors.validation.secondaryHex"));
    if (accentHex && !isValidHexColor(accentHex)) errors.push(tBranding("colors.validation.accentHex"));
    return errors;
  }, [primaryHex, secondaryHex, accentHex, tBranding]);

  const handleSave = async () => {
    if (validationErrors.length > 0) {
      toast({
        title: tBranding("colors.validation.invalidValuesTitle"),
        description: validationErrors[0],
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      await update({
        enabled,
        colors: {
          primaryHex: normalizeHex(primaryHex),
          secondaryHex: normalizeHex(secondaryHex),
          accentHex: normalizeHex(accentHex),
        },
        typography: { fontFamily, scale },
        texts: { de: textsDe, en: textsEn },
      });
      setHasChanges(false);
      toast({
        title: tBranding("toasts.saved.title"),
        description: tBranding("toasts.saved.description"),
      });
    } catch (error) {
      toast({
        title: tCommon("error.title", "Error"),
        description: error instanceof Error ? error.message : tCommon("error.unknown", "Unknown error"),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const uploadLogo = async (variant: "primary" | "square" | "favicon", file: File) => {
    const allowedTypes =
      variant === "favicon"
        ? ["image/png", "image/x-icon", "image/vnd.microsoft.icon", "image/svg+xml"]
        : ["image/png", "image/jpeg"];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: tBranding("toasts.invalidFileType.title"),
        description:
          variant === "favicon"
            ? tBranding("toasts.invalidFileType.faviconDescription", { defaultValue: "Please upload a PNG, ICO, or SVG file." })
            : tBranding("toasts.invalidFileType.description"),
        variant: "destructive",
      });
      return;
    }
    if (file.size > 100 * 1024) {
      toast({
        title: tBranding("toasts.fileTooLarge.title"),
        description: tBranding("toasts.fileTooLarge.description"),
        variant: "destructive",
      });
      return;
    }

    setIsUploading(variant);
    // Show immediate local preview while upload is in-flight.
    const objectUrl = URL.createObjectURL(file);
    if (variant === "primary") {
      if (localPrimaryPreviewUrl) URL.revokeObjectURL(localPrimaryPreviewUrl);
      setLocalPrimaryPreviewUrl(objectUrl);
    } else if (variant === "square") {
      if (localSquarePreviewUrl) URL.revokeObjectURL(localSquarePreviewUrl);
      setLocalSquarePreviewUrl(objectUrl);
    } else {
      if (localFaviconPreviewUrl) URL.revokeObjectURL(localFaviconPreviewUrl);
      setLocalFaviconPreviewUrl(objectUrl);
    }
    try {
      const rawUrl = await generateUploadUrl({});
      // generateUploadUrl() may return a relative path (e.g. /api/storage/upload?token=...).
      // Resolve it against the Convex API URL so the POST goes to Convex, not the Vite dev server.
      const url = rawUrl.startsWith("http")
        ? rawUrl
        : `${(import.meta.env.VITE_CONVEX_URL || "http://localhost:3210").replace(/\/$/, "")}${rawUrl}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!res.ok) {
        throw new Error(tBranding("errors.uploadFailed", { status: res.status }));
      }
      const json = (await res.json()) as { storageId?: string };
      if (!json.storageId) {
        throw new Error(tBranding("errors.uploadMissingStorageId"));
      }

      await setLogo({
        variant,
        storageId: json.storageId as any,
      });

      // Build a one-off polling URL with a fresh timestamp to bypass browser cache
      // while we wait for Convex storage propagation. The displayed URL uses the
      // reactive `updatedAt` from the database, which updates via the Convex query.
      const pollBust = Date.now();
      const remoteSrc = getLogoUrl(variant, pollBust);

      // Wait until the HTTP endpoint is actually returning the newly-uploaded storage object.
      // If it still serves the default SVG fallback, keep the local blob preview.
      const storageReady = await waitForRemoteLogoToBeStorageBacked({
        src: remoteSrc,
        expectedStorageId: json.storageId,
        timeoutMs: 6000,
      });
      const remoteLoads = storageReady ? await preloadImage(remoteSrc, 2500) : false;

      // Only switch away from the local object URL once we know the remote endpoint is serving the new asset.
      if (storageReady && remoteLoads) {
        URL.revokeObjectURL(objectUrl);
        if (variant === "primary") {
          setLocalPrimaryPreviewUrl((prev) => (prev === objectUrl ? null : prev));
        } else if (variant === "square") {
          setLocalSquarePreviewUrl((prev) => (prev === objectUrl ? null : prev));
        } else {
          setLocalFaviconPreviewUrl((prev) => (prev === objectUrl ? null : prev));
        }
      }

      const toastDescriptionKey =
        variant === "primary"
          ? "toasts.uploaded.primaryDescription"
          : variant === "square"
            ? "toasts.uploaded.squareDescription"
            : "toasts.uploaded.faviconDescription";
      toast({
        title: tBranding("toasts.uploaded.title"),
        description: tBranding(toastDescriptionKey),
      });

      if (!storageReady || !remoteLoads) {
        toast({
          title: tBranding("toasts.previewNotLoaded.title", {
            defaultValue: "Preview not loaded",
          }),
          description: tBranding("toasts.previewNotLoaded.description", {
            defaultValue:
              "Logo was uploaded, but the server preview could not be loaded. Check VITE_CONVEX_SITE_URL / mixed-content settings.",
          }),
          variant: "destructive",
        });
      }
    } catch (error) {
      // Keep the local preview visible on error; the server config may not have been updated.
      toast({
        title: tCommon("error.title", "Error"),
        description: error instanceof Error ? error.message : tCommon("error.unknown", "Unknown error"),
        variant: "destructive",
      });
    } finally {
      setIsUploading(null);
    }
  };

  const removeLogo = async (variant: "primary" | "square" | "favicon") => {
    setIsUploading(variant);
    try {
      await setLogo({ variant });
      if (variant === "primary") {
        if (localPrimaryPreviewUrl) URL.revokeObjectURL(localPrimaryPreviewUrl);
        setLocalPrimaryPreviewUrl(null);
      } else if (variant === "square") {
        if (localSquarePreviewUrl) URL.revokeObjectURL(localSquarePreviewUrl);
        setLocalSquarePreviewUrl(null);
      } else {
        if (localFaviconPreviewUrl) URL.revokeObjectURL(localFaviconPreviewUrl);
        setLocalFaviconPreviewUrl(null);
      }
      const toastDescriptionKey =
        variant === "primary"
          ? "toasts.removed.primaryDescription"
          : variant === "square"
            ? "toasts.removed.squareDescription"
            : "toasts.removed.faviconDescription";
      toast({
        title: tBranding("toasts.removed.title"),
        description: tBranding(toastDescriptionKey),
      });
    } catch (error) {
      toast({
        title: tCommon("error.title", "Error"),
        description: error instanceof Error ? error.message : tCommon("error.unknown", "Unknown error"),
        variant: "destructive",
      });
    } finally {
      setIsUploading(null);
    }
  };

  if (branding === undefined) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-80" />
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const primaryLogoUrl = getLogoUrl("primary", updatedAt);
  const squareLogoUrl = getLogoUrl("square", updatedAt);
  const faviconUrl = getLogoUrl("favicon", updatedAt);
  const primaryPreviewSrc = localPrimaryPreviewUrl ?? primaryLogoUrl;
  const squarePreviewSrc = localSquarePreviewUrl ?? squareLogoUrl;
  const faviconPreviewSrc = localFaviconPreviewUrl ?? faviconUrl;

  return (
    <div className="space-y-6">
      <PageHeader
        title={tBranding("page.title")}
        description={tBranding("page.description")}
        actions={
          <Button onClick={handleSave} disabled={isSaving || validationErrors.length > 0}>
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {tCommon("actions.save", "Save")}
          </Button>
        }
      />

      <div className="flex items-start gap-3 rounded-lg border border-info-border bg-info-muted p-4">
        <Palette className="h-5 w-5 text-info mt-0.5" />
        <div>
          <p className="font-medium text-info-muted-foreground">
            {tBranding("info.title")}
          </p>
          <p className="text-sm text-info-muted-foreground/80">
            {tBranding("info.description", {
              internalHost: "app.{domain}",
              clientHost: "portal.{domain}",
            })}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{tBranding("sections.general.title")}</CardTitle>
          <CardDescription>{tBranding("sections.general.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label>{tBranding("general.customBranding.label")}</Label>
              <p className="text-sm text-muted-foreground">
                {tBranding("general.customBranding.help")}
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={(v: boolean) => {
                setEnabled(v);
                setHasChanges(true);
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{tBranding("sections.logos.title")}</CardTitle>
          <CardDescription>{tBranding("sections.logos.description")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-3">
          <div className="space-y-3">
            <Label>{tBranding("logos.primary.label")}</Label>
            <div className="flex items-center gap-4">
              <div className="h-16 w-52 rounded-md border bg-white flex items-center justify-center overflow-hidden">
                <img
                  src={primaryPreviewSrc}
                  alt={tBranding("logos.primary.alt")}
                  className="max-h-14 max-w-[12rem] object-contain"
                />
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={primaryInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="sr-only"
                  disabled={isUploading !== null}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) void uploadLogo("primary", f);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={isUploading !== null}
                  onClick={() => primaryInputRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {isUploading === "primary"
                    ? tBranding("logos.uploading")
                    : tBranding("logos.upload")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="justify-start"
                  disabled={isUploading !== null}
                  onClick={() => void removeLogo("primary")}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {tBranding("logos.remove")}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{tBranding("logos.fileHint")}</p>
          </div>

          <div className="space-y-3">
            <Label>{tBranding("logos.square.label")}</Label>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-md border bg-white flex items-center justify-center overflow-hidden">
                <img
                  src={squarePreviewSrc}
                  alt={tBranding("logos.square.alt")}
                  className="max-h-14 max-w-14 object-contain"
                />
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={squareInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="sr-only"
                  disabled={isUploading !== null}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) void uploadLogo("square", f);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={isUploading !== null}
                  onClick={() => squareInputRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {isUploading === "square"
                    ? tBranding("logos.uploading")
                    : tBranding("logos.upload")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="justify-start"
                  disabled={isUploading !== null}
                  onClick={() => void removeLogo("square")}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {tBranding("logos.remove")}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{tBranding("logos.fileHint")}</p>
          </div>

          <div className="space-y-3">
            <Label>{tBranding("logos.favicon.label", { defaultValue: "Favicon" })}</Label>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-md border bg-white flex items-center justify-center overflow-hidden">
                <img
                  src={faviconPreviewSrc}
                  alt={tBranding("logos.favicon.alt", { defaultValue: "Favicon" })}
                  className="h-8 w-8 object-contain"
                />
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={faviconInputRef}
                  type="file"
                  accept="image/png,.ico,image/svg+xml"
                  className="sr-only"
                  disabled={isUploading !== null}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) void uploadLogo("favicon", f);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={isUploading !== null}
                  onClick={() => faviconInputRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {isUploading === "favicon"
                    ? tBranding("logos.uploading")
                    : tBranding("logos.upload")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="justify-start"
                  disabled={isUploading !== null}
                  onClick={() => void removeLogo("favicon")}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {tBranding("logos.remove")}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {tBranding("logos.favicon.fileHint", { defaultValue: "PNG/ICO/SVG, max 100KB." })}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{tBranding("sections.colors.title")}</CardTitle>
          <CardDescription>{tBranding("sections.colors.description")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="primaryHex">{tBranding("colors.fields.primary")}</Label>
            <Input
              id="primaryHex"
              value={primaryHex}
              onChange={(e) => {
                setPrimaryHex(e.target.value);
                setHasChanges(true);
              }}
              placeholder="#2f5b45"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="secondaryHex">{tBranding("colors.fields.secondary")}</Label>
            <Input
              id="secondaryHex"
              value={secondaryHex}
              onChange={(e) => {
                setSecondaryHex(e.target.value);
                setHasChanges(true);
              }}
              placeholder="#f5f4f2"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="accentHex">{tBranding("colors.fields.accent")}</Label>
            <Input
              id="accentHex"
              value={accentHex}
              onChange={(e) => {
                setAccentHex(e.target.value);
                setHasChanges(true);
              }}
              placeholder="#3c6a52"
            />
          </div>
          {validationErrors.length > 0 && (
            <p className="text-sm text-error md:col-span-3">{validationErrors[0]}</p>
          )}
          <div className="rounded-lg border bg-muted/30 p-4 md:col-span-3">
            <p className="text-sm font-medium mb-3">{tBranding("colors.preview.title")}</p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="h-10 rounded-md px-4 text-sm font-semibold text-white"
                style={{
                  background: `linear-gradient(180deg, ${primaryHex} 0%, ${accentHex} 100%)`,
                }}
              >
                {tBranding("colors.preview.primaryAction")}
              </button>
              <button
                type="button"
                className="h-10 rounded-md border px-4 text-sm font-medium"
                style={{ borderColor: secondaryHex, color: "#1f2937" }}
              >
                {tBranding("colors.preview.secondary")}
              </button>
              <button
                type="button"
                className="text-sm font-medium underline"
                style={{ color: primaryHex }}
              >
                {tBranding("colors.preview.link")}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{tBranding("sections.typography.title")}</CardTitle>
          <CardDescription>{tBranding("sections.typography.description")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label>{tBranding("typography.fontFamily")}</Label>
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={fontFamily}
              onChange={(e) => {
                setFontFamily(e.target.value as any);
                setHasChanges(true);
              }}
            >
              <option value="lato">{tBranding("typography.fontFamilyOptions.latoDefault")}</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>{tBranding("typography.textSize")}</Label>
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={scale}
              onChange={(e) => {
                setScale(e.target.value as any);
                setHasChanges(true);
              }}
            >
              <option value="standard">{tBranding("typography.scaleOptions.standard")}</option>
              <option value="large">{tBranding("typography.scaleOptions.large")}</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{tBranding("sections.texts.title")}</CardTitle>
          <CardDescription>{tBranding("sections.texts.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setLocale("de")}
              className={`rounded-md px-3 py-2 text-sm font-medium ${
                locale === "de"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {tBranding("texts.locale.de")}
            </button>
            <button
              type="button"
              onClick={() => setLocale("en")}
              className={`rounded-md px-3 py-2 text-sm font-medium ${
                locale === "en"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {tBranding("texts.locale.en")}
            </button>
          </div>

          <div className="grid gap-4">
            {TEXT_FIELDS.map((field) => (
              <div key={field.key} className="space-y-2">
                <div className="flex items-baseline justify-between gap-4">
                  <Label htmlFor={`txt-${locale}-${field.key}`}>{tBranding(field.labelKey)}</Label>
                  <span className="text-xs text-muted-foreground font-mono">{field.key}</span>
                </div>
                <Input
                  id={`txt-${locale}-${field.key}`}
                  value={textState[field.key] ?? ""}
                  onChange={(e) => {
                    setTextState((prev) => ({ ...prev, [field.key]: e.target.value }));
                    setHasChanges(true);
                  }}
                  placeholder={tBranding("texts.placeholder")}
                />
                {field.helperKey && (
                  <p className="text-xs text-muted-foreground">{tBranding(field.helperKey)}</p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {hasChanges && (
        <div className="flex items-center justify-end">
          <Button onClick={handleSave} disabled={isSaving || validationErrors.length > 0}>
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {tCommon("actions.save", "Save")}
          </Button>
        </div>
      )}
    </div>
  );
}

export function Branding() {
  return (
    <SuperAdminGuard>
      <BrandingContent />
    </SuperAdminGuard>
  );
}
